import React, { useState } from 'react';
import {
  Box, Button, Typography, Chip, CircularProgress, Alert, Tabs, Tab,
  IconButton, Tooltip, Paper, TextField,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import TerminalRoundedIcon from '@mui/icons-material/TerminalRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import toast from 'react-hot-toast';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 3 }}>{children}</Box> : null;
}

export default function Phase5Output({
  output,
  gptProfile,
  loading,
  error,
  onBack,
  onRestart,
  onSaveAsAgent,
  agentSaved,
  onUpdateOutput,
  onUpdateProfile,
}) {
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Local edit state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSystemPrompt, setEditSystemPrompt] = useState('');
  const [editCapabilities, setEditCapabilities] = useState([]);
  const [editVoorWie, setEditVoorWie] = useState('');
  const [editUitblinkers, setEditUitblinkers] = useState('');
  const [editWanneer, setEditWanneer] = useState('');
  const [newCapability, setNewCapability] = useState('');

  const startEditing = () => {
    setEditName(gptProfile?.naam || '');
    setEditDescription(gptProfile?.rolBeschrijving || '');
    setEditSystemPrompt(output.systemPrompt || '');
    setEditCapabilities([...(output.capabilities || [])]);
    setEditVoorWie(output.profileSummary?.voorWie || '');
    setEditUitblinkers(output.profileSummary?.uitblinkers || '');
    setEditWanneer(output.profileSummary?.wanneerInzetten || '');
    setEditing(true);
  };

  const saveEdits = () => {
    onUpdateProfile({
      ...gptProfile,
      naam: editName,
      rolBeschrijving: editDescription,
    });
    onUpdateOutput({
      ...output,
      systemPrompt: editSystemPrompt,
      capabilities: editCapabilities,
      profileSummary: {
        ...output.profileSummary,
        voorWie: editVoorWie,
        uitblinkers: editUitblinkers,
        wanneerInzetten: editWanneer,
      },
    });
    setEditing(false);
    toast.success('Wijzigingen opgeslagen');
  };

  const addCapability = () => {
    if (newCapability.trim()) {
      setEditCapabilities([...editCapabilities, newCapability.trim()]);
      setNewCapability('');
    }
  };

  const removeCapability = (index) => {
    setEditCapabilities(editCapabilities.filter((_, i) => i !== index));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} gekopieerd!`);
  };

  const downloadAsFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAll = () => {
    const fullExport = `# ${gptProfile?.naam || 'Super GPT'} - Complete Configuratie
Generated by SuperAgents Builder

---

## SYSTEM PROMPT

${output.systemPrompt}

---

## PROFILE SUMMARY

Voor wie: ${output.profileSummary?.voorWie}
Uitblinkers: ${output.profileSummary?.uitblinkers}
Wanneer inzetten: ${output.profileSummary?.wanneerInzetten}

---

## KNOWLEDGE MAP

### Thema's
${(output.knowledgeMap?.themas || []).map(t => `- ${t}`).join('\n')}

### Expert-invloeden
${(output.knowledgeMap?.expertInvloeden || []).map(t => `- ${t}`).join('\n')}

### Content-typen
${(output.knowledgeMap?.contentTypen || []).map(t => `- ${t}`).join('\n')}

### Frameworks
${(output.knowledgeMap?.frameworks || []).map(t => `- ${t}`).join('\n')}

### Voorbeeldonderwerpen
${(output.knowledgeMap?.voorbeeldOnderwerpen || []).map(t => `- ${t}`).join('\n')}

---

## CAPABILITIES

${(output.capabilities || []).map(c => `- ${c}`).join('\n')}

---

## OUTPUT TEMPLATES

${(output.outputTemplates || []).map(t => `### ${t.naam}
${t.beschrijving}

${t.structuur}
`).join('\n---\n\n')}
`;

    const safeName = (gptProfile?.naam || 'SuperGPT').replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_').slice(0, 100);
    downloadAsFile(fullExport, `${safeName}_configuratie.md`);
    toast.success('Complete configuratie gedownload!');
  };

  if (loading) {
    return (
      <div className="gpt-loading">
        <CircularProgress size={48} />
        <div className="gpt-loading-text">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Super GPT wordt gebouwd...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System prompt, capabilities en templates worden gegenereerd.
          </Typography>
        </div>
        <div className="gpt-loading-dots">
          <span>•</span><span>•</span><span>•</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gpt-phase">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>Terug</Button>
      </div>
    );
  }

  if (!output) return null;

  return (
    <div className="gpt-phase">
      {/* Success Header */}
      <Paper sx={{
        p: 3, mb: 3,
        background: 'linear-gradient(135deg, #ecfdf5 0%, #eef2ff 100%)',
        border: '1px solid #a7f3d0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            {editing ? (
              <TextField
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                variant="standard"
                InputProps={{ sx: { fontSize: '1.5rem', fontWeight: 800, color: '#065f46' } }}
                fullWidth
              />
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#065f46', display: 'flex', alignItems: 'center', gap: 1 }}>
                <RocketLaunchRoundedIcon />
                {gptProfile?.naam || 'Super GPT'} is klaar!
              </Typography>
            )}
            {editing ? (
              <TextField
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                variant="standard"
                multiline
                fullWidth
                sx={{ mt: 1 }}
                InputProps={{ sx: { fontSize: '0.875rem', color: '#6b7280' } }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {gptProfile?.rolBeschrijving || 'Je Super GPT is succesvol gebouwd.'}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {editing ? (
              <Button
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                onClick={saveEdits}
                sx={{ background: '#6366f1' }}
              >
                Wijzigingen opslaan
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={<EditRoundedIcon />}
                onClick={startEditing}
              >
                Bewerken
              </Button>
            )}
            {agentSaved ? (
              <Button
                variant="contained"
                startIcon={<CheckCircleRoundedIcon />}
                disabled
                sx={{ background: '#10b981 !important', color: '#fff !important' }}
              >
                Toegevoegd aan AI Chat
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SmartToyRoundedIcon />}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await onSaveAsAgent();
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving || editing}
              >
                {saving ? 'Opslaan...' : 'Toevoegen aan AI Chat'}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={exportAll}
              disabled={editing}
            >
              Exporteren
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<TerminalRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="System Prompt" />
          <Tab icon={<InfoRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Profiel" />
          <Tab icon={<MapRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Knowledge Map" />
          <Tab icon={<BuildRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Capabilities" />
          <Tab icon={<ArticleRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Templates" />
        </Tabs>
      </Box>

      {/* Tab 0: System Prompt */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Tooltip title="Kopieer system prompt">
            <IconButton onClick={() => copyToClipboard(editing ? editSystemPrompt : output.systemPrompt, 'System prompt')}>
              <ContentCopyRoundedIcon />
            </IconButton>
          </Tooltip>
          {!editing && (
            <Tooltip title="Download als bestand">
              <IconButton onClick={() => downloadAsFile(output.systemPrompt, 'system_prompt.txt')}>
                <DownloadRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {editing ? (
          <TextField
            value={editSystemPrompt}
            onChange={(e) => setEditSystemPrompt(e.target.value)}
            multiline
            fullWidth
            minRows={15}
            maxRows={40}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                fontSize: '0.85rem',
                lineHeight: 1.7,
                background: '#1f2937',
                color: '#e5e7eb',
                borderRadius: 3,
              },
            }}
          />
        ) : (
          <div className="gpt-system-prompt">
            {output.systemPrompt}
          </div>
        )}
      </TabPanel>

      {/* Tab 1: Profile Summary */}
      <TabPanel value={tab} index={1}>
        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Voor wie" value={editVoorWie} onChange={(e) => setEditVoorWie(e.target.value)} multiline rows={2} fullWidth />
            <TextField label="Waar blinkt deze GPT in uit?" value={editUitblinkers} onChange={(e) => setEditUitblinkers(e.target.value)} multiline rows={2} fullWidth />
            <TextField label="Wanneer inzetten?" value={editWanneer} onChange={(e) => setEditWanneer(e.target.value)} multiline rows={2} fullWidth />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <div className="gpt-output-section">
              <h3>Voor wie</h3>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {output.profileSummary?.voorWie}
              </Typography>
            </div>
            <div className="gpt-output-section">
              <h3>Uitblinkers</h3>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {output.profileSummary?.uitblinkers}
              </Typography>
            </div>
            <div className="gpt-output-section">
              <h3>Wanneer inzetten</h3>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {output.profileSummary?.wanneerInzetten}
              </Typography>
            </div>
          </Box>
        )}
      </TabPanel>

      {/* Tab 2: Knowledge Map */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <div className="gpt-output-section">
            <h3>Thema's</h3>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(output.knowledgeMap?.themas || []).map((t, i) => (
                <Chip key={i} label={t} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </div>
          <div className="gpt-output-section">
            <h3>Expert-invloeden</h3>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(output.knowledgeMap?.expertInvloeden || []).map((t, i) => (
                <Chip key={i} label={t} size="small" sx={{ backgroundColor: '#ede9fe', color: '#5b21b6' }} />
              ))}
            </Box>
          </div>
          <div className="gpt-output-section">
            <h3>Frameworks</h3>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(output.knowledgeMap?.frameworks || []).map((t, i) => (
                <Chip key={i} label={t} size="small" sx={{ backgroundColor: '#fef3c7', color: '#92400e' }} />
              ))}
            </Box>
          </div>
          <div className="gpt-output-section">
            <h3>Voorbeeldonderwerpen</h3>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(output.knowledgeMap?.voorbeeldOnderwerpen || []).map((t, i) => (
                <Box key={i} sx={{ p: 1, background: '#f9fafb', borderRadius: 1, fontSize: '0.85rem' }}>
                  {t}
                </Box>
              ))}
            </Box>
          </div>
        </Box>
      </TabPanel>

      {/* Tab 3: Capabilities */}
      <TabPanel value={tab} index={3}>
        {editing ? (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                value={newCapability}
                onChange={(e) => setNewCapability(e.target.value)}
                placeholder="Nieuwe capability toevoegen..."
                size="small"
                fullWidth
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCapability(); } }}
              />
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addCapability} disabled={!newCapability.trim()}>
                Toevoegen
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {editCapabilities.map((cap, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.5, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 2,
                    display: 'flex', alignItems: 'center', gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>{cap}</Typography>
                  <IconButton size="small" onClick={() => removeCapability(i)} sx={{ color: '#ef4444' }}>
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {(output.capabilities || []).map((cap, i) => (
              <Box
                key={i}
                sx={{
                  p: 2, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 3,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#a5b4fc', boxShadow: '0 2px 8px rgba(99,102,241,0.08)' },
                }}
              >
                <Box sx={{
                  width: 32, height: 32, borderRadius: 2,
                  background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6366f1', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{cap}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Tab 4: Output Templates */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(output.outputTemplates || []).map((template, i) => (
            <div key={i} className="gpt-output-section">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3>{template.naam}</h3>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.beschrijving}
                  </Typography>
                </div>
                <Tooltip title="Kopieer template">
                  <IconButton size="small" onClick={() => copyToClipboard(template.structuur, template.naam)}>
                    <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{
                p: 2, background: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb',
                fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem',
                lineHeight: 1.7, whiteSpace: 'pre-wrap',
              }}>
                {template.structuur}
              </Box>
            </div>
          ))}
        </Box>
      </TabPanel>

      {/* Bottom Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack} disabled={editing}>
          Terug
        </Button>
        <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={onRestart} disabled={editing}>
          Nieuwe GPT bouwen
        </Button>
      </Box>
    </div>
  );
}
